import { isFunction } from "lodash";

import "./jwplayer.css";

export default class JWPlayerAdapter {
  constructor(container, options = {}) {
    this.container = container;
    this.options = options;
    this.playlistSource = undefined;
    this.player = null;
  }

  // INITIALIZATION //

  loadScriptWithRetry = (scriptSrc, maxRetries, retryInterval) => {
    let retries = 0;

    function loadScript(onSuccess, onFailure) {
      const script = document.createElement("script");
      script.src = scriptSrc;
      script.crossOrigin = "anonymous";

      script.onload = onSuccess;
      script.onerror = () => {
        retries += 1;
        if (retries > maxRetries) {
          onFailure(new Error("Failed to load script"));
        } else {
          setTimeout(() => {
            loadScript(onSuccess, onFailure);
          }, retryInterval);
        }
        document.head.removeChild(script);
      };
      document.head.appendChild(script);
    }

    return new Promise((resolve, reject) => {
      loadScript(resolve, reject);
    });
  };

  setup = async () => {
    if (!window.jwplayer) {
      await this.loadScriptWithRetry(
        process.env.NEXT_PUBLIC_JW_LIBRARY_SCRIPT,
        3,
        1000,
      );
    }

    this.player = window.jwplayer(this.container);
    this.player.setup({
      playlist: `//content.jwplatform.com/v2/media/${this.options.source}`,
      related: {
        displayMode: "shelf",
        onclick: "link",
      },
      advertising: { client: "vast", tag: this.options.adTag },
      autostart: this.options.autostart,
      stretching: this.options.stretching,
    });

    if (this.options.playlist) {
      this.player.setPlaylistItemCallback((item) => {
        // eslint-disable-next-line no-param-reassign
        item.recommendations = `//content.jwplatform.com/v2/playlists/${this.options.playlist}`;
      });
    }

    this.player.on("mute", ({ mute }) => {
      this.options.mute = mute;
    });

    return new Promise((resolve, reject) => {
      this.player.once(
        "setupError",
        (e = { message: "unknown setup error" }) => {
          reject(e);
        },
      );
      this.player.once("ready", () => resolve());
    });
  };

  setSource = (source) => {
    this.options.source = source;
  };

  // CONFIGURATIONS //

  getSource = () => this.options.source;

  getState = () => isFunction(this.player?.getState) && this.player.getState();

  getAutostart = () => this.options.autostart;

  setPlaylist = (playlistSource) => {
    if (
      isFunction(this.player?.setPlaylistItemCallback) &&
      this.playlistSource !== playlistSource
    ) {
      this.playlistSource = playlistSource;
      this.player.setPlaylistItemCallback((item) => {
        // eslint-disable-next-line no-param-reassign
        item.recommendations = `//content.jwplatform.com/v2/playlists/${playlistSource}`;
      });
    }
  };

  setAutostart = (autostart) => {
    this.options.autostart = autostart;
    setTimeout(() => {
      if (isFunction(this.player?.setConfig))
        this.player.setConfig({ autostart });
    }, 0);
  };

  setStretching = (stretching) => {
    this.options.stretching = stretching;
    setTimeout(() => {
      if (isFunction(this.player?.setConfig))
        this.player.setConfig({ stretching });
    }, 0);
  };

  setMute = (mute) => {
    this.options.mute = mute;
    setTimeout(() => {
      if (isFunction(this.player?.setConfig)) this.player.setConfig({ mute });
    }, 0);
  };

  // PLAYBACK //
  play = () => {
    if (isFunction(this.player?.play)) this.player.play();
  };

  pause = () => {
    if (isFunction(this.player?.pause)) this.player.pause();
  };

  load = (source) => {
    if (isFunction(this.player?.load) && source !== this.options.source) {
      this.setSource(source);
      this.player.load(`//content.jwplatform.com/v2/media/${source}`);
    }
  };

  // EVENTS //

  onFullScreen = (callback = () => {}) => {
    if (isFunction(this.player?.on)) this.player.on("fullscreen", callback);

    return () => isFunction(this.player?.off) && this.player.off("fullscreen");
  };

  onAdComplete = (callback = () => {}) => {
    if (isFunction(this.player?.on)) this.player.on("adComplete", callback);

    return () => isFunction(this.player?.off) && this.player.off("adComplete");
  };

  onAdStart = (callback = () => {}) => {
    if (isFunction(this.player?.on)) this.player.on("adImpression", callback);

    return () =>
      isFunction(this.player?.off) && this.player.off("adImpression");
  };

  onDisplayClick = (callback = () => {}) => {
    if (isFunction(this.player?.on)) this.player.on("displayClick", callback);
    return () =>
      isFunction(this.player?.off) && this.player.off("displayClick");
  };

  onTime = (callback = () => {}) => {
    if (isFunction(this.player?.on)) this.player.on("time", callback);
    return () => isFunction(this.player?.off) && this.player.off("time");
  };

  onFirstPlay = (callback = () => {}) => {
    if (isFunction(this.player?.on)) this.player.on("firstFrame", callback);
    return () => isFunction(this.player?.off) && this.player.off("firstFrame");
  };

  onPlay = (callback = () => {}) => {
    if (isFunction(this.player?.on)) this.player.on("play", callback);
    return () => isFunction(this.player?.off) && this.player.off("play");
  };

  onPause = (callback = () => {}) => {
    if (isFunction(this.player?.on)) this.player.on("pause", callback);
    return () => isFunction(this.player?.off) && this.player.off("pause");
  };

  onComplete = (callback = () => {}) => {
    if (isFunction(this.player?.on)) this.player.on("complete", callback);
    return () => isFunction(this.player?.off) && this.player.off("complete");
  };

  onError = (callback = () => {}) => {
    if (isFunction(this.player?.on)) {
      this.player.on("error", (e = { message: "unknown playback error" }) => {
        callback(e);
      });
    }

    return () => isFunction(this.player?.off) && this.player.off("error");
  };

  on = (event, callback) =>
    isFunction(this.player?.on) && this.player.on(event, callback);

  once = (event, callback) =>
    isFunction(this.player?.once) && this.player.once(event, callback);

  off = (event) => isFunction(this.player?.off) && this.player.off(event);

  // CLEAR //
  remove = () => {
    if (isFunction(this.player?.remove)) {
      this.player.remove();
      this.player = null;
    }
  };
}
